import { useCallback, useEffect, useMemo } from 'react';
import { useTracking } from 'react-tracking';
import {
  Text,
  Box,
  Grid,
  Row,
  Column,
  Button,
  useTheme,
} from '@simplywallst/ui-core';
import CrossSolid from '@simplywallst/ui-core/icons/CrossSolid';
import TickSolid from '@simplywallst/ui-core/icons/TickSolid';

import {
  formatMonthlyPrice,
  formatYearlyPrice,
  getDiscountedPlanAmount,
  getYearlyPriceDiscounted,
} from '@/components/OurPlans/components/Plans/utils';
import Tooltip from '@/components/Tooltip';
import { useUserPlans } from '@/hooks/useUserPlans';
import { useUserAccess } from '@/hooks/useUserAccess';
import useUser, { CouponState } from '@/hooks/useUser';
import {
  AddedFeatures,
  BackgroundImage,
  FeatureItem,
  StyledText,
} from './styled';
import { PlanButton } from '../PlanButton/PlanButton';
import {
  BillingPeriod,
  DefaultButtonState,
  GrandfatherCoupons,
  usePlanButtonState,
  UserStatus,
  userStatusData,
} from '../../constants';
import type { ButtonState, Plan } from '../../interface';
import { useLocale } from '@/hooks/useLocale';
import { useTranslation } from '@/hooks/useTranslation';
import { PlanType } from '@simplywallst/services';
import { formatDistanceToNow, fromUnixTime } from 'date-fns';
import { Tile } from '@/components/TileNew';
import { usePlanIllustration } from '../../hooks/usePlanIllustration';
import { LoadingSkeleton } from './LoadingSkeleton';

interface Props {
  darkBackground?: boolean;
  hideFreePlan?: boolean;
  userPlan: Plan;
  coupon: CouponState;
  onClose?: () => void;
  onAction?: () => void;
}

const getUserStatus = (
  hasSeenTrialEndModal: boolean,
  isUserLoggedIn: boolean,
  planStatus: string,
  userPlan: Plan
): UserStatus => {
  if (!isUserLoggedIn) return UserStatus.guest;
  if (hasSeenTrialEndModal !== true && userPlan.type === 'free')
    return UserStatus.trialEnded;
  if (userPlan.type === 'free') return UserStatus.free;
  if (planStatus === 'trialing') return UserStatus.trial;
  return userStatusData[userPlan.type][userPlan.interval]
    ? userStatusData[userPlan.type][userPlan.interval].status
    : UserStatus.guest;
};

type InclusionPillProps = {
  children: string;
  darkBackground: boolean;
};
const InclusionPill = ({ children, darkBackground }: InclusionPillProps) => (
  <Box
    backgroundColor={darkBackground ? 'noir' : 'white'}
    borderRadius="x1_5"
    width="max-content"
    padding="x0_75"
    paddingLeft="x1_5"
    paddingRight="x1_5"
  >
    <Text
      typography="small"
      color={darkBackground ? 'whiteRGBA' : 'blackRGBA'}
      opacity={0.7}
    >
      {children}
    </Text>
  </Box>
);

export const PlansContent = ({
  darkBackground = false,
  hideFreePlan,
  userPlan,
  coupon,
  onAction,
  onClose,
}: Props) => {
  const theme = useTheme();
  const { data: userData } = useUser();
  const { data: userAccessData } = useUserAccess();
  const { status, plans } = useUserPlans();
  const billingPeriod = 'year';
  const { trackEvent } = useTracking({
    type: 'track',
  });
  const locale = useLocale();
  const { getButtonState } = usePlanButtonState();
  const getPlanBtnState = (
    plan: Plan,
    userPlan: Plan,
    billingPeriod: BillingPeriod
  ): ButtonState => {
    const planType = plan.type;
    const userStatus = getUserStatus(
      userData.hasSeenTrialEndModal,
      userData.loggedIn,
      userData.billing.status,
      userPlan
    );
    const planBtnState = getButtonState(
      userData.billing.canceled,
      userData.billing.canceledAt,
      userPlan.name
    );
    if (
      (planType === 'premium' || planType === 'unlimited') &&
      (userStatus === UserStatus.premiumMonthly ||
        userStatus === UserStatus.premiumYearly ||
        userStatus === UserStatus.unlimitedMonthly ||
        userStatus === UserStatus.unlimitedYearly)
    ) {
      return planBtnState[planType][userStatus][billingPeriod]
        ? planBtnState[planType][userStatus][billingPeriod]
        : DefaultButtonState;
    }
    return planBtnState[planType][userStatus]
      ? planBtnState[planType][userStatus]
      : DefaultButtonState;
  };

  //Temporarily show grandfather discount for unlimited plan only, until
  //backend supports multiple coupons per plan and user
  const isGrandfatherCoupon = GrandfatherCoupons.includes(
    userData.billing.coupon.id
  );

  const mappedPlans = useMemo(() => {
    if (status !== 'success') {
      return [];
    }
    return plans
      .filter((plan) => {
        const planValues = Object.values(plan)[0];
        if (planValues.type === 'advisor') return false;

        if (planValues.type === 'free' && !hideFreePlan) return true;
        if (
          planValues.type === 'free' &&
          !hideFreePlan &&
          planValues.interval === billingPeriod &&
          planValues.visible
        )
          return true;
        if (
          planValues.type !== 'free' &&
          planValues.interval === billingPeriod &&
          planValues.visible
        )
          return true;
        else return false;
      })
      .map((plan) => Object.values(plan)[0]);
  }, [plans, status, billingPeriod, hideFreePlan]);

  const getMonthlyPriceDiscounted = useCallback(
    (plan: Plan) => {
      const discountedPlanAmount = getDiscountedPlanAmount(
        plan.amount,
        coupon.percentOff
      );
      const planAmount =
        (isGrandfatherCoupon && plan.type === 'unlimited') ||
        !isGrandfatherCoupon
          ? discountedPlanAmount
          : plan.amount;
      return formatMonthlyPrice(
        planAmount,
        plan.interval,
        plan.currencyISO,
        locale
      );
    },
    [coupon.percentOff, isGrandfatherCoupon, locale]
  );

  useEffect(() => {
    if (!mappedPlans.length) {
      return;
    }

    const plans: Record<string, string>[] = mappedPlans.map((plan) => ({
      plan: plan.name,
      price: getMonthlyPriceDiscounted(plan),
      coupon: coupon.id,
    }));

    trackEvent({
      action: 'landed',
      plans: plans,
    });
  }, [coupon, getMonthlyPriceDiscounted, mappedPlans, trackEvent]);

  const t = useTranslation();

  const featureList = useMemo(() => {
    return [
      'companyReport',
      'companiesAnalysedInPortfolio',
      'watchlist',
      'stockScreener',
      'risksRewards',
    ] as const;
  }, []);

  function getPlanBlurb(plan: PlanType) {
    switch (plan) {
      case 'free':
        return t('plan.free.blurb.exp');
      case 'premium':
        return t('plan.premium.blurb.exp');
      case 'unlimited':
        return t('plan.unlimited.blurb.exp');
      default:
        return '';
    }
  }

  const illustration = usePlanIllustration();
  return (
    <>
      <Box
        marginTop="x5"
        marginLeftMd="x3"
        marginRightMd="x3"
        marginLeftLg="x0"
        marginRightLg="x0"
      >
        <Grid
          templateColumns="340px"
          templateColumnsMd={hideFreePlan ? '355px 355px' : '1fr 1fr 1fr'}
          gap="24px"
          justifyContent="center"
        >
          {mappedPlans.map((plan) => {
            const monthlyPriceDiscounted = getMonthlyPriceDiscounted(plan);
            const yearlyPriceDiscounted = getYearlyPriceDiscounted(
              plan.amount,
              plan.type,
              coupon.percentOff,
              isGrandfatherCoupon
            );
            const yearlyPriceDiscountedFormatted = formatYearlyPrice(
              yearlyPriceDiscounted,
              plan.currencyISO,
              locale
            );
            const { action, btnStyle, cta } = getPlanBtnState(
              plan,
              userPlan,
              billingPeriod
            );

            const showDiscount = plan.type !== 'free' && coupon.id !== '';

            return (
              <Box key={plan.name}>
                <Box
                  marginLeft="x3"
                  backgroundColor="chart06"
                  width="max-content"
                  paddingLeft="x2"
                  paddingRight="x2"
                  borderTopLeftRadius="x1"
                  borderTopRightRadius="x1"
                  style={{
                    visibility:
                      showDiscount && coupon.redeemBy > 0
                        ? 'visible'
                        : 'hidden',
                  }}
                >
                  <Grid alignItems="center" height="24px">
                    <Text typography="xSmall" color="white">
                      Offer expires in{' '}
                      {formatDistanceToNow(
                        fromUnixTime(coupon.redeemBy / 1000)
                      )}
                    </Text>
                  </Grid>
                </Box>
                <Box boxShadow="softShadow" borderRadius="x1">
                  <Tile>
                    <BackgroundImage planType={plan.type} couponId={coupon.id}>
                      <Box width="100%" height="162px" padding="x3">
                        <Box position="absolute" bottom="0" right="0">
                          <img
                            alt="bear-illustration"
                            srcSet={illustration[plan.type]}
                            width="148px"
                          />
                        </Box>
                        <Box position="absolute">
                          <Text
                            color="noir"
                            typography="xLargeStrong"
                            textAlign="left"
                          >
                            {plan.name}
                          </Text>
                          <Text
                            typography="xSmall"
                            color="spaceGrayRGBA"
                            opacity={0.9}
                            textAlign="left"
                          >
                            {getPlanBlurb(plan.type)}
                          </Text>
                          <Grid
                            templateColumns="max-content max-content"
                            alignItems="baseline"
                            gap="8px"
                          >
                            <Text
                              typography="small"
                              color="spaceGrayRGBA"
                              opacity={0.7}
                              data-cy-id={`${plan.type}-price`}
                            >
                              {plan.currencyISO}{' '}
                              <mark>{monthlyPriceDiscounted}</mark>
                              {plan.type === 'free' ? ' forever' : ' /mo'}
                            </Text>
                            {showDiscount && (
                              <Text
                                typography="small"
                                color="spaceGrayRGBA"
                                opacity={0.5}
                                style={{
                                  textDecoration: 'line-through',
                                }}
                              >
                                {formatMonthlyPrice(
                                  plan.amount,
                                  plan.interval,
                                  plan.currencyISO,
                                  locale
                                )}
                                {' /mo'}
                              </Text>
                            )}
                          </Grid>
                          <Grid
                            templateColumns="max-content max-content"
                            alignItems="baseline"
                            gap="8px"
                          >
                            <Text
                              typography="tiny"
                              color="spaceGrayRGBA"
                              opacity={0.7}
                            >
                              {plan.type !== 'free' &&
                                `${yearlyPriceDiscountedFormatted} ${t(
                                  'modal.upgrade.billedYearly'
                                )}`}
                            </Text>
                            {showDiscount && (
                              <Text
                                typography="tiny"
                                color="spaceGrayRGBA"
                                opacity={0.5}
                                style={{
                                  textDecoration: 'line-through',
                                }}
                              >
                                {formatYearlyPrice(
                                  plan.amount,
                                  plan.currencyISO,
                                  locale
                                )}
                              </Text>
                            )}
                          </Grid>
                        </Box>
                      </Box>
                    </BackgroundImage>
                    <Box width="100%" padding="x3" backgroundColor="white">
                      <ul>
                        {featureList.map((feature) => {
                          let displayText = '';

                          if (plan.type === 'free') {
                            if (feature === 'companyReport') {
                              const isUsingUserAccessData =
                                userAccessData.brokerReferrer &&
                                userAccessData.viewCompanyReports;
                              const defaultLimitPlan =
                                plan.uacCompaniesAnalysis || 5;
                              const limitViewReportsPerMonth =
                                isUsingUserAccessData
                                  ? userAccessData.viewCompanyReports
                                  : defaultLimitPlan;
                              displayText = t(
                                'plan.feature.limitCompanyReports',
                                {
                                  count: limitViewReportsPerMonth,
                                }
                              );
                            }

                            if (feature === 'companiesAnalysedInPortfolio') {
                              displayText = t('plan.feature.singlePortfolio', {
                                portfolioCount: 1,
                                holdingsCount: 10,
                              });
                            }

                            if (feature === 'watchlist') {
                              displayText = t('plan.feature.limitedWatchlist', {
                                count: 1,
                              });
                            }
                            if (feature === 'stockScreener') {
                              displayText = t(
                                'plan.feature.freeLimitStockScreener'
                              );
                            }
                            if (feature === 'risksRewards') {
                              displayText = t('plan.feature.limitedRiskReward');
                            }
                          }
                          if (plan.type === 'premium') {
                            if (feature === 'companyReport') {
                              displayText = t(
                                'plan.feature.limitCompanyReports',
                                {
                                  count: 30,
                                }
                              );
                            }
                            if (feature === 'companiesAnalysedInPortfolio') {
                              displayText = t('plan.feature.limitedPortfolio', {
                                portfolioCount: 3,
                                holdingsCount: 30,
                              });
                            }
                            if (feature === 'stockScreener') {
                              displayText = t(
                                'plan.feature.limitStockScreener',
                                {
                                  count: 3,
                                }
                              );
                            }

                            if (feature === 'watchlist') {
                              displayText = t('plan.feature.limitedWatchlist', {
                                count: 3,
                              });
                            }
                            if (feature === 'risksRewards') {
                              displayText = displayText = t(
                                'plan.feature.unlimitedRiskReward'
                              );
                            }
                          }
                          if (plan.type === 'unlimited') {
                            if (feature === 'companyReport') {
                              displayText = t(
                                'plan.feature.unlimitedCompanyReports'
                              );
                            }
                            if (feature === 'companiesAnalysedInPortfolio') {
                              displayText = t(
                                'plan.feature.unlimitedHoldings',
                                {
                                  portfolioCount: 5,
                                }
                              );
                            }

                            if (feature === 'watchlist') {
                              displayText = t('plan.feature.limitedWatchlist', {
                                count: 5,
                              });
                            }
                            if (feature === 'stockScreener') {
                              displayText = t(
                                'plan.feature.limitStockScreener',
                                {
                                  count: 10,
                                }
                              );
                            }
                            if (feature === 'risksRewards') {
                              displayText = displayText = t(
                                'plan.feature.unlimitedRiskReward'
                              );
                            }
                          }

                          return (
                            <Box
                              marginBottom="x0_75"
                              key={`${plan.type}-${feature}`}
                            >
                              <li key={feature}>
                                <Text
                                  typography="small"
                                  color="spaceGray"
                                  textAlign="left"
                                >
                                  {displayText}
                                </Text>
                              </li>
                            </Box>
                          );
                        })}
                        <Grid
                          templateColumns="24px 1fr 24px 1fr"
                          gap="4px"
                          justifyContent="left"
                          alignItems="flex-start"
                          paddingTop="x2"
                        >
                          {plan.type === 'free' ? (
                            <>
                              <CrossSolid
                                fill={theme.color.charcoal}
                                opacity={0.3}
                              />
                              <Text
                                typography="small"
                                color="spaceGrayRGBA"
                                opacity={0.5}
                                textAlign="left"
                                aria-label="No Brokerage Sync"
                              >
                                {t('plan.feature.brokerage.sync')}
                              </Text>
                              <CrossSolid
                                fill={theme.color.charcoal}
                                opacity={0.3}
                              />
                              <Text
                                typography="small"
                                color="spaceGrayRGBA"
                                opacity={0.5}
                                textAlign="left"
                                aria-label="No Priority Support"
                              >
                                {t('plan.feature.priority.support')}
                              </Text>
                            </>
                          ) : (
                            <>
                              <TickSolid fill={theme.color.brand01} />
                              <Text
                                typography="small"
                                color="spaceGray"
                                textAlign="left"
                              >
                                {t('plan.feature.brokerage.sync')}
                              </Text>
                              <TickSolid fill={theme.color.brand01} />
                              <Text
                                typography="small"
                                color="spaceGray"
                                textAlign="left"
                              >
                                {t('plan.feature.priority.support')}
                              </Text>
                            </>
                          )}
                        </Grid>
                      </ul>
                      {((RUNTIME_ENV === 'native' && action === 'continue') ||
                        RUNTIME_ENV !== 'native') && (
                        <Box marginTop="x3">
                          <PlanButton
                            btnStyle={btnStyle}
                            action={action}
                            plan={plan}
                            isLoggedIn={userData.loggedIn}
                            couponId={coupon.id}
                            onClick={onAction}
                            onComplete={onClose}
                          >
                            {cta}
                          </PlanButton>
                        </Box>
                      )}
                    </Box>
                  </Tile>
                </Box>
              </Box>
            );
          })}
          {status === 'pending' && (
            <>
              <LoadingSkeleton />
              <LoadingSkeleton />
              <LoadingSkeleton />
            </>
          )}
        </Grid>
      </Box>
      <Box
        width="100%"
        paddingTop="x2"
        paddingLeft="x2"
        paddingLeftMd={hideFreePlan ? 'x10' : 'x0'}
        paddingRight="x2"
        paddingRightMd={hideFreePlan ? 'x10' : 'x2'}
      >
        <Row justifyContent="center">
          {mappedPlans.length > 0 &&
            !hideFreePlan &&
            RUNTIME_ENV !== 'native' && (
              <Column displayFlex order={1} orderXl={0} col={12} colXl={7}>
                <Grid
                  width="100%"
                  justifyContent="center"
                  justifyContentXl="flex-start"
                >
                  <AddedFeatures darkBackground={darkBackground}>
                    <FeatureItem notIncluded={false}>
                      <TickSolid fill={theme.color.brand01} />
                      {t('modal.upgrade.footer.taxDeductible')}
                    </FeatureItem>
                    <FeatureItem notIncluded={false}>
                      <TickSolid fill={theme.color.brand01} />
                      {t('modal.upgrade.footer.moneyBack')}
                    </FeatureItem>
                    <FeatureItem notIncluded={false}>
                      <TickSolid fill={theme.color.brand01} />
                      {t('modal.upgrade.footer.cancel')}
                    </FeatureItem>
                  </AddedFeatures>
                </Grid>
              </Column>
            )}
          <Column
            displayFlex
            order={0}
            orderXl={1}
            col={12}
            colXl={5}
            justifyContentSm="center"
            justifyContentLg="flex-end"
          >
            <Box paddingRight="x0" paddingBottomSm="x1">
              <Tooltip
                title={
                  <Box maxWidth="360px" padding="x1">
                    <Text
                      textAlign="center"
                      typography="xSmall"
                      color="spaceGray"
                    >
                      {t('plan.unlimited.tooltip.portfolioAndScreenerLimits')}
                    </Text>
                  </Box>
                }
                light
                placement="top"
                zIndex={99999}
              >
                <Grid
                  placeContent="center"
                  placeContentXl={hideFreePlan ? 'center' : 'end'}
                >
                  <StyledText darkBackground={darkBackground}>
                    {t('plan.unlimited.footnote.portfolioAndScreenerLimits')}
                  </StyledText>
                </Grid>
              </Tooltip>
            </Box>
          </Column>
        </Row>
      </Box>

      <Box
        marginTop="x4"
        marginBottom="x3"
        marginLeft="x5"
        marginRight="x5"
        marginLeftMd="x20"
        marginRightMd="x20"
      >
        <Grid templateColumns="1fr" width="100%" gap="16px">
          <Text
            typography="small"
            color={darkBackground ? 'whiteRGBA' : 'blackRGBA'}
            opacity={0.5}
            textAlign="center"
          >
            {t('plan.feature.include')}
          </Text>
          <Row alignItem="center" gap="8px" justifyContent="center">
            <InclusionPill darkBackground={darkBackground}>
              {t('plan.feature.global.market.access')}
            </InclusionPill>
            <InclusionPill darkBackground={darkBackground}>
              {t('plan.feature.snp.global.data')}
            </InclusionPill>
            <InclusionPill darkBackground={darkBackground}>
              {t('plan.feature.weekly.market.newsletter')}
            </InclusionPill>
            <InclusionPill darkBackground={darkBackground}>
              {t('plan.feature.fair.value.narrative')}
            </InclusionPill>
            <InclusionPill darkBackground={darkBackground}>
              {t('plan.feature.stock.notes.alerts')}
            </InclusionPill>
            <InclusionPill darkBackground={darkBackground}>
              {t('plan.feature.chrome.extension')}
            </InclusionPill>
            <InclusionPill darkBackground={darkBackground}>
              {t('plan.feature.weekly.portfolio.update.email')}
            </InclusionPill>
          </Row>
          <Grid width="100%" justifyContent="center">
            <a
              href="/?view"
              onClick={() =>
                trackEvent({
                  modifier: 'view_all_features_button',
                  action: 'clicked',
                  plans: plans,
                })
              }
            >
              <Button styleType={darkBackground ? 'louderLarge' : 'loudLarge'}>
                {t('plan.feature.view.all.features')}
              </Button>
            </a>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
